import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import LearnerConfidenceInput from "../../capabilities/learner-confidence/LearnerConfidenceInput";
import PairedScaleResponseInput from "../../capabilities/paired-scale/PairedScaleResponseInput";
import ReferenceAnchorPlayer from "../../capabilities/reference-anchors/ReferenceAnchorPlayer";
import AnchorTour from "../../capabilities/reference-anchors/AnchorTour";
import AmbiguityDistributionFeedback from "../../capabilities/ambiguity-feedback/AmbiguityDistributionFeedback";
import PerceptualResponseInput from "../../primitives/perceptual-response/PerceptualResponseInput";
import CategoricalReferenceFeedback from "../../capabilities/reference-feedback/CategoricalReferenceFeedback";
import ContinuousReferenceFeedback from "../../capabilities/reference-feedback/ContinuousReferenceFeedback";
import ExpertBandFeedback from "../../capabilities/expert-band/ExpertBandFeedback";
import ExpertBandReferenceHelp from "../../capabilities/expert-band/ExpertBandReferenceHelp";
import TargetAudioPlayer from "../../capabilities/target-playback/TargetAudioPlayer";
import { listeningConditionStatementPresentation } from "../../capabilities/listening-condition/listeningConditionStatementPresentation";
import {
  formatGovernedDate,
  renderExactTokenizedCopy,
  temporalCopyTokens,
} from "../../../program-presentation/programTemporalTokenPresentation";
import CitationsPanel from "../../../../components/citations/CitationsPanel";
import { pvqdVoiceDatabaseCitation } from "../../../../components/citations/voiceCitationResources";
import ApplicationContextualHelp from "../../../../components/application/ApplicationContextualHelp";
import useBYCETaskController from "./useBYCETaskController";
import { isAcceptedByceProgramIdentity } from "../../byceKsuIdentity";
import {
  BYCE_CATEGORICAL_OPTIONS,
  BYCE_CONSTRUCT_PRESENTATIONS,
  adaptBYCECategoricalReference,
  adaptBYCEContinuousReferenceRows,
  adaptBYCEPairedCategoricalReferenceRows,
  getBYCEAnchorDisplayLabel,
  getBYCEConstructPresentation,
  selectBYCEVoiceAssessmentReference,
} from "./bycePresentation";

const BYCE_SESSION_REVIEW_PRESENTATION = Object.freeze({
  programSlug: "building-your-clinical-ear-session-review-program",
  reflectionPromptVersion: "governor-default-g11.v1",
  reflectionPrompt: "Why was this voice genuinely hard to rate? Use the spread of expert ratings to explain your answer.",
});

function CategoricalReferenceHelp({ sourceParameterLabel }) {
  return (
    <ApplicationContextualHelp
      label={`About PVQD categorical ratings for ${sourceParameterLabel}`}
      title={`About the ${sourceParameterLabel} reference`}
    >
      <p>These ratings come from the published PVQD perceptual evaluations for this voice sample. The distribution shows how the ratings were spread across Normal, Mild, Moderate, and Severe. The median provides a descriptive central reference; it is not one uniquely exact answer.</p>
    </ApplicationContextualHelp>
  );
}

function ContinuousReferenceHelp() {
  return (
    <ApplicationContextualHelp
      label="About reference feedback"
      title="About reference feedback"
    >
      <p>The graph shows the individual PVQD reference ratings for this voice. Your rating is shown separately for comparison. Central value summarizes the reference ratings, and Spread (SD) describes how much those ratings varied. These values are descriptive references, not a required correct answer.</p>
    </ApplicationContextualHelp>
  );
}

function GlobalSeverityBridgeHelp() {
  return (
    <span className="av-methodology-heading">
      <span>Grade and Overall Severity</span>
      <ApplicationContextualHelp
        label="About the Grade and Overall Severity bridge"
        title="About the two global voice-severity scales"
      >
        <p>Grade and Overall Severity are used here as corresponding global voice-severity judgments on different scales. The continuous rating is a new perceptual judgment of the same voice, not a numerical conversion of the categorical rating.</p>
      </ApplicationContextualHelp>
    </span>
  );
}

function JudgmentOrientation({ reviewPresentation, activeConstruct, constructLabel }) {
  const indicator = reviewPresentation?.dimensionIndicator;
  const constructIds = indicator?.constructs || [];
  if (!reviewPresentation?.stageInstruction && !constructIds.length) return null;
  const currentOnly = indicator?.activeMode === "current_construct";
  return (
    <section className="byce-task__orientation" aria-label="Judgment orientation">
      {reviewPresentation.stageInstruction && <p><strong>Instruction:</strong> {reviewPresentation.stageInstruction}</p>}
      {reviewPresentation.dominantQualityGuidance && <p>{reviewPresentation.dominantQualityGuidance} Rate only <strong>{constructLabel}</strong> for this Voice.</p>}
      {reviewPresentation.constructGuidance?.[activeConstruct] && <p className="byce-task__construct-guidance">{reviewPresentation.constructGuidance[activeConstruct]}</p>}
      {constructIds.length > 0 && <>
        <p className="av-group-heading">Voice dimensions</p>
        <ul className="byce-task__dimension-indicator" aria-label="Voice dimensions">
          {constructIds.map(id => {
            const constructPresentation = getBYCEConstructPresentation(id);
            const label = constructPresentation.continuous?.sourceParameterLabel || constructPresentation.label;
            const active = !currentOnly || id === activeConstruct;
            return <li key={id} aria-current={currentOnly && active ? "true" : undefined} className={active ? "is-active" : ""}>{label}</li>;
          })}
        </ul>
      </>}
    </section>
  );
}

function enterPrimaryPhase(ref) {
  const applicationContent = document.querySelector(".app-content");
  if (applicationContent) applicationContent.scrollTop = 0;
  ref.current?.focus?.({ preventScroll: true });
}

function ValidationSummary({ issues }) {
  if (!issues.length) return null;
  return (
    <section className="byce-task__validation-summary" role="alert" aria-live="assertive" aria-atomic="true">
      <strong>Complete the required responses before submitting.</strong>
      <ul>{issues.map(issue => <li key={issue.key}>{issue.message}</li>)}</ul>
    </section>
  );
}

export default function BYCETask() {
  const { t, i18n } = useTranslation();
  const controller = useBYCETaskController();
  const {
    program, step, task, trial, stimulus, capabilities, config, activeConstructConfig, sample, anchors, value,
    setValue, confidence, setConfidence, vectorValues, setVectorValues,
    vectorConfidence, setVectorConfidence, paired, pairedValue, setPairedValue,
    pairedConfidence, setPairedConfidence, feedback, text, setText, error,
    activeConstruct, replay, recordTargetPlay, complete, submitSimple, pairedAction,
    submitReflection, submitVector, canSubmitSimple, canSubmitVector,
    currentIndex, totalTrials,
    acknowledgementRequired, acknowledgementRequirement, listeningConditionsAcknowledged,
    acknowledgementInFlight, acknowledgeListeningConditions,
    anchorTourRequired, anchorTour, anchorTourDismissed, anchorTourInFlight,
    playAnchorTourItem, continueFromAnchorTour,
    targetPlaybackGateRequired, targetPlaybackSatisfied,
    persistedTrialResponse, currentResponseLocked, lockedTransitionPending, retryLockedTransition,
    sessionStartPresentation, guidedIntroductionRequired,
    guidedIntroductionDismissed, continueFromGuidedIntroduction,
    reflectionContext, selectedReflectionTrialId, setSelectedReflectionTrialId,
    governedReflectionRequired, submissionInFlight,
    completionPresentation, continueFromCompletionPresentation,
    temporalDelivery, participantTimeZone, programScopedLocale,
  } = controller;
  const supportContextRef = useRef(null);
  const targetPlaybackRef = useRef(null);
  const simpleRatingRef = useRef(null);
  const simpleConfidenceRef = useRef(null);
  const vectorRatingRefs = useRef({});
  const vectorConfidenceRefs = useRef({});
  const reflectionChoiceRef = useRef(null);
  const reflectionTextRef = useRef(null);
  const [validationAttempted, setValidationAttempted] = useState(false);
  const expertBandCapability = capabilities.expertBandFeedback;
  // The runtime projector must resolve the governed trial role. Do not infer a
  // role from a one-entry declaration: first-pass probes deliberately remain
  // silent even in Activities that also contain supported targets.
  const repeatedJudgmentCapability = capabilities.repeatedJudgment;
  const repeatedJudgmentPolicy = repeatedJudgmentCapability?.trialPolicies?.find(policy =>
    policy.trialRole === trial?.governedTrialRole);
  const expertBandVariant = feedback
    ? expertBandCapability?.variants?.find(variant => variant.trialRole === trial?.governedTrialRole)
    : undefined;
  const trialStartRef = useRef(null);
  const primaryViewKey = acknowledgementRequired && !listeningConditionsAcknowledged
    ? "listening-acknowledgement"
    : anchorTourRequired && !anchorTourDismissed
      ? "anchor-tour"
      : guidedIntroductionRequired && !guidedIntroductionDismissed
        ? "guided-introduction"
        : feedback
        ? "feedback"
        : `voice:${trial?.trialId}`;
  const priorPrimaryViewRef = useRef(primaryViewKey);
  const priorConstructContextRef = useRef({ activeConstruct, trialId: trial?.trialId });

  useEffect(() => {
    const prior = priorConstructContextRef.current;
    if (prior.trialId === trial?.trialId && prior.activeConstruct && prior.activeConstruct !== activeConstruct) {
      enterPrimaryPhase(supportContextRef);
    }
    priorConstructContextRef.current = { activeConstruct, trialId: trial?.trialId };
  }, [activeConstruct, trial?.trialId]);

  useEffect(() => {
    if (priorPrimaryViewRef.current !== primaryViewKey) {
      enterPrimaryPhase(trialStartRef);
    }
    priorPrimaryViewRef.current = primaryViewKey;
  }, [primaryViewKey]);

  useEffect(() => {
    setValidationAttempted(false);
  }, [trial?.trialId]);

  if (!trial || !stimulus) return <p>No provisional trial is available.</p>;

  const constructPresentation = getBYCEConstructPresentation(activeConstruct);
  const constructLabel = constructPresentation.label;
  const scalePresentation = constructPresentation[config.scaleFamily] || {};
  const simpleConstructId = activeConstructConfig?.id || config.constructId;
  const simpleParameterId = activeConstructConfig?.parameterId || config.sourceParameterId || simpleConstructId;
  const contextualStep = controller.session?.effectiveRuntimeConfiguration?.step;
  const reviewPresentation = config.reviewPresentation;
  const presentedStep = step || contextualStep;
  const presentedPhase = program?.phases?.find(phase => phase.key === presentedStep?.phase);
  const isReflection = presentedStep?.learnerUnit?.type === "reflection" || task.responseType === "text";
  const isSessionReviewConsumer = program?.programSlug === BYCE_SESSION_REVIEW_PRESENTATION.programSlug;
  const showWithinSessionVoiceProgress = reviewPresentation?.voiceProgress === true;
  const programLabel = program?.title || program?.titleKey || reviewPresentation?.programLabel || "Building Your Clinical Ear";
  const stageIndex = reviewPresentation?.stageIndex || presentedPhase?.order;
  const stageTitle = reviewPresentation?.stageTitle || presentedPhase?.title || presentedPhase?.titleKey?.replace(/^Stage \d+ — /, "");
  const currentStepIndex = program?.steps?.findIndex(candidate => candidate.key === presentedStep?.key) ?? -1;
  const nextProgramStep = currentStepIndex >= 0 ? program?.steps?.[currentStepIndex + 1] : null;
  const finalFeedbackActionLabel = presentedStep?.learnerUnit?.presentation?.composite?.role === "rating" || nextProgramStep?.learnerUnit?.type === "reflection"
    ? "Continue to reflection"
    : "Continue";
  const listeningConditionCopy = listeningConditionStatementPresentation(acknowledgementRequirement);
  const listeningSessionPlacement = program?.learnerPresentation?.listeningSessionPlacement;
  const stageIntroductionAcknowledgement = presentedStep?.learnerUnit?.presentation
    ?.stageIntroductionAcknowledgement;
  const stageIntroductionTokens = temporalCopyTokens(temporalDelivery, {
    locale: programScopedLocale?.contentLang || i18n.language,
    participantTimeZone,
  });
  const stageIntroductionAcknowledgementBlocks = stageIntroductionAcknowledgement?.contractVersion ===
    "byce-g3-punchlist-19-stage-introductions.v1"
    ? (stageIntroductionAcknowledgement.blocks || []).map(block => ({
        ...block,
        text: renderExactTokenizedCopy(block.template, stageIntroductionTokens),
      })).filter(block => block.text)
    : [];
  const governedG3Placement = listeningSessionPlacement?.contractVersion ===
    "byce-g3-listening-session-placement.v1" &&
    listeningSessionPlacement.applicableStageKeys?.includes(presentedStep?.phase);
  const activationValidationEnabled = isAcceptedByceProgramIdentity({
    slug: program?.programSlug,
    version: program?.programVersion,
  });
  const hideGenericJudgmentConfidence = governedG3Placement &&
    listeningSessionPlacement.ratingHeader?.genericJudgmentConfidence === "hidden";
  const confidenceExplanation = governedG3Placement &&
    listeningSessionPlacement.confidenceExplanation?.placement === "listening-conditions"
    ? listeningSessionPlacement.confidenceExplanation.text
    : null;
  const confidenceMidpointLabel = capabilities.learnerConfidence?.presentation?.midpointLabel === "none" ? undefined : "Neutral";
  const missingRatingTemplate = config.validationPresentation?.requiredRating?.message;
  const missingRatingMessage = missingRatingTemplate?.replace("{parameter}", constructLabel);
  const firstMissingVectorConstruct = (config.constructs || []).find(construct =>
    vectorValues[construct.id] === "" || vectorValues[construct.id] === undefined
  );
  const vectorMissingRatingMessage = missingRatingTemplate?.replace(
    "{parameter}",
    firstMissingVectorConstruct?.label || getBYCEConstructPresentation(firstMissingVectorConstruct?.id).label
  );
  const targetPlaybackMessage = capabilities.targetPlaybackEvidence?.submitGate?.validationMessage;
  const persistedValue = (...keys) => keys.reduce((resolved, key) => (
    resolved !== undefined ? resolved : persistedTrialResponse?.response?.[key]
  ), undefined);
  const persistedConfidence = (...constructIds) => {
    const acceptedIds = new Set(constructIds.filter(Boolean).map(String));
    const evidence = (persistedTrialResponse?.learnerConfidence || []).find(entry =>
      acceptedIds.has(String(entry.constructId))
    );
    return Number.isFinite(Number(evidence?.value)) ? Number(evidence.value) : undefined;
  };
  const simpleFeedbackValue = value !== "" && value !== undefined
    ? value
    : persistedValue(simpleParameterId, simpleConstructId);
  const categoricalFeedback = adaptBYCECategoricalReference({ constructId: simpleConstructId, learnerValue: simpleFeedbackValue, reference: feedback?.reference });
  const pairedCategoricalFeedbackRows = adaptBYCEPairedCategoricalReferenceRows({ constructs: capabilities.pairedScale?.constructs, pairedState: paired, reference: feedback?.reference?.categorical });
  const pairedContinuousFeedbackRows = adaptBYCEContinuousReferenceRows({ constructs: capabilities.pairedScale?.constructs, pairedState: paired, reference: feedback?.reference?.continuous || feedback?.reference });
  const vectorContinuousFeedbackRows = (config.constructs || []).map(construct => ({
    parameterId: construct.parameterId,
    constructLabel: construct.label || getBYCEConstructPresentation(construct.id).label,
    sourceParameterLabel: construct.label || construct.parameterId,
    learnerValue: vectorValues[construct.id] !== "" && vectorValues[construct.id] !== undefined
      ? vectorValues[construct.id]
      : persistedValue(construct.parameterId, construct.id),
    learnerConfidence: persistedConfidence(construct.id, construct.parameterId) ??
      (persistedTrialResponse ? undefined : vectorConfidence[construct.id]),
    reference: selectBYCEVoiceAssessmentReference(feedback?.reference, construct.parameterId),
    panelRaterMeans: feedback?.reference?.panelRaterMeans?.[construct.parameterId]?.values,
  }));
  const simpleContinuousFeedbackRows = config.scaleFamily === "continuous" ? [{
    parameterId: simpleParameterId,
    constructLabel,
    sourceParameterLabel: scalePresentation.sourceParameterLabel || simpleParameterId,
    learnerValue: simpleFeedbackValue,
    learnerConfidence: persistedConfidence(simpleConstructId, simpleParameterId) ??
      (persistedTrialResponse ? undefined : confidence),
    reference: selectBYCEVoiceAssessmentReference(feedback?.reference, simpleParameterId),
    panelRaterMeans: feedback?.reference?.panelRaterMeans?.[simpleParameterId]?.values,
  }] : [];
  const guidedIntroductionAnchors = [...new Map(
    anchors.map(anchor => [String(anchor.stimulusInstanceId), anchor])
  ).values()];
  const sharedNormalAnchor = guidedIntroductionAnchors.find(anchor => anchor.role === "normal");
  const guidedIntroductionAnchorGroups = capabilities.referenceAnchors?.presentation?.groups?.map(group => ({
    ...group,
    ...(group.label === "Overall Severity" ? { label: "Overall severity" } : {}),
    ...(group.groupId === "all-parameters" ? {
      label: "Baseline",
      anchorIds: sharedNormalAnchor ? [sharedNormalAnchor.anchorId] : group.anchorIds,
    } : {}),
  }));
  const preVoiceGateActive =
    (acknowledgementRequired && !listeningConditionsAcknowledged) ||
    (anchorTourRequired && !anchorTourDismissed) ||
    (guidedIntroductionRequired && !guidedIntroductionDismissed);
  const mirror = feedback?.repeatedJudgmentMirror;
  const firstPassDate = mirror?.firstPass?.completionTimeZone
    ? formatGovernedDate(mirror.firstPass.completedAt, {
        locale: i18n.language,
        displayContext: { mode: "participant_local" },
        participantTimeZone: mirror.firstPass.completionTimeZone,
      })
    : null;
  const mirrorCopy = repeatedJudgmentPolicy?.feedbackPolicy === "mirror-then-reference"
    ? {
        message: repeatedJudgmentPolicy.messages?.[0],
        framingMessage: repeatedJudgmentPolicy.messages?.[1],
      }
    : null;

  const opening = presentedStep?.learnerUnit?.presentation?.opening;
  const instruction = presentedStep?.learnerUnit?.presentation?.instruction;
  const citationReferences = sample?.presentation?.references || {};
  const judgmentPhase = !currentResponseLocked && !feedback;
  const sessionInstructionHidden = governedG3Placement &&
    listeningSessionPlacement.sessionInstruction?.hiddenProgramStepKeys?.includes(presentedStep?.key);
  const showSessionInstruction = !governedG3Placement || (
    listeningSessionPlacement.sessionInstruction?.visibility === "first-rating-trial-judgment-only" &&
    !sessionInstructionHidden && currentIndex === 0 && judgmentPhase && !preVoiceGateActive
  );
  const showSessionOpening = Boolean(opening?.blockId && opening?.template) &&
    currentIndex === 0 &&
    (!governedG3Placement || listeningSessionPlacement.sessionOpening?.placement !== "session-card");
  const showListeningConditionExplanation = !governedG3Placement || (
    listeningSessionPlacement.listeningConditionExplanation?.visibility === "session-1.1-first-listening-page-only" &&
    presentedStep?.key === listeningSessionPlacement.listeningConditionExplanation.programStepKey &&
    currentIndex === 0 && !feedback
  );
  const anchorInstruction = governedG3Placement
    ? listeningSessionPlacement.anchorInstruction?.text
    : capabilities.referenceAnchors?.presentation?.guidance;
  const showStandaloneTargetReplayInstruction = !governedG3Placement ||
    listeningSessionPlacement.standaloneFeedbackReplayInstruction?.visibility !== "hidden";
  const feedbackBandPresentation = program?.learnerPresentation?.feedbackBand;
  const governedG3FeedbackBand = feedbackBandPresentation?.contractVersion ===
    "byce-g3-punchlist-19-feedback-band.v1" &&
    feedbackBandPresentation.applicableStageKeys?.includes(presentedStep?.phase)
    ? feedbackBandPresentation
    : null;
  const governedG3FeedbackVariant = presentedStep?.phase === "stage-3" ? "stage3" : "ordinary";
  const selectedReflectionCandidate = reflectionContext?.candidates?.find(candidate =>
    candidate.sourceTrialId === selectedReflectionTrialId);
  const exactReflectionPrompt = selectedReflectionCandidate?.promptTokenState === "configured"
    ? selectedReflectionCandidate.resolvedPrompt
    : reflectionContext?.promptTokenState === "configured" ? task.presentation?.resolvedPrompt : null;
  const provisionalReflectionPrompt = selectedReflectionCandidate?.promptTokenState === "focus_parameter_unresolved"
    || reflectionContext?.promptTokenState === "focus_parameter_unresolved"
    ? reflectionContext.provisionalPrompt
    : null;
  const reflectionPromptAvailable = Boolean(selectedReflectionTrialId && (exactReflectionPrompt || provisionalReflectionPrompt));
  const targetPlaybackIssue = targetPlaybackGateRequired && !targetPlaybackSatisfied
    ? { key: "target-playback", message: targetPlaybackMessage || "Play the target voice before submitting.", ref: targetPlaybackRef }
    : null;
  const simpleValidationIssues = [
    targetPlaybackIssue,
    value === "" || value === undefined
      ? { key: "rating", message: missingRatingMessage || `Choose a ${constructLabel} rating before submitting.`, ref: simpleRatingRef }
      : null,
    confidence === undefined
      ? { key: "confidence", message: "Choose a confidence rating before submitting.", ref: simpleConfidenceRef }
      : null,
  ].filter(Boolean);
  const vectorValidationIssues = [
    targetPlaybackIssue,
    ...(config.constructs || []).flatMap(construct => {
      const label = construct.label || getBYCEConstructPresentation(construct.id).label;
      return [
        vectorValues[construct.id] === "" || vectorValues[construct.id] === undefined
          ? { key: `rating:${construct.id}`, message: missingRatingTemplate?.replace("{parameter}", label) || `Choose a ${label} rating before submitting.`, ref: { current: vectorRatingRefs.current[construct.id] } }
          : null,
        vectorConfidence[construct.id] === undefined
          ? { key: `confidence:${construct.id}`, message: `Choose a confidence rating for ${label}.`, ref: { current: vectorConfidenceRefs.current[construct.id] } }
          : null,
      ].filter(Boolean);
    }),
  ].filter(Boolean);
  const reflectionMinimum = Number(task.responseConfig?.minCharacters || 0);
  const reflectionValidationIssues = [
    governedReflectionRequired && !selectedReflectionTrialId
      ? { key: "reflection-voice", message: "Choose one Voice before submitting the Reflection.", ref: reflectionChoiceRef }
      : null,
    selectedReflectionTrialId && text.trim().length < reflectionMinimum
      ? { key: "reflection-text", message: `Write at least ${reflectionMinimum} characters before submitting the Reflection.`, ref: reflectionTextRef }
      : null,
  ].filter(Boolean);
  const activateValidatedSubmission = (issues, submit) => {
    if (!activationValidationEnabled) return submit();
    setValidationAttempted(true);
    if (issues.length) {
      const firstIncomplete = issues[0].ref?.current;
      firstIncomplete?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      firstIncomplete?.focus?.();
      return;
    }
    setValidationAttempted(false);
    submit();
  };

  if (completionPresentation?.text) {
    return (
      <main className="byce-task">
        <section className="byce-task__surface" aria-label="Session complete" data-copy-block={completionPresentation.blockId}>
          {completionPresentation.text.split(/\n+/).map(paragraph => <p key={paragraph}>{paragraph}</p>)}
          <button className="app-btn app-btn-primary" type="button" onClick={continueFromCompletionPresentation}>Continue</button>
        </section>
      </main>
    );
  }

  return (
    <main className="byce-task">
      {task.uiConfig?.developmentPreview && !program?.learnerPresentation?.suppressDevelopmentPreview && <aside className="byce-task__notice">Development preview — structure and samples are provisional and not curator-approved.</aside>}
      <header ref={trialStartRef} tabIndex="-1" className="byce-task__header" aria-label="Current trial start">
        <p className="byce-task__eyebrow">{programLabel}</p>
        {stageIndex && <p>Stage {stageIndex} of {reviewPresentation?.stageTotal || program?.phases?.length || 4}</p>}
        {stageTitle && <h2>Stage {stageIndex} — {stageTitle}</h2>}
        <h3>{isReflection
          ? presentedStep?.learnerUnit?.presentation?.composite?.sessionTitleKey || "Reflection"
          : presentedStep?.learnerUnit?.type === "listening_session"
            ? t(presentedStep.titleKey, { defaultValue: presentedStep.titleKey })
          : t(task.titleKey, { defaultValue: task.title || "Development activity" })}</h3>
        {!preVoiceGateActive && showWithinSessionVoiceProgress && <div className="byce-task__trial-progress">
          <p>Voice {currentIndex + 1} of {totalTrials || 1}</p>
          <progress value={currentIndex + 1} max={totalTrials || 1} aria-label={`Voice ${currentIndex + 1} of ${totalTrials || 1}`} />
        </div>}
        {!governedG3Placement && !feedback && acknowledgementRequired && listeningConditionsAcknowledged && <p>{listeningConditionCopy.reminder}</p>}
        {!feedback && !acknowledgementRequired && config.reviewPresentation?.listeningConditionAcknowledgement === "concept-placeholder" && <p>Use headphones or earphones, or listen in a quiet environment. Durable listening-condition acknowledgement is not yet available.</p>}
        {judgmentPhase && !preVoiceGateActive && task.responseType !== "text" && !hideGenericJudgmentConfidence && <p>Listen to the target voice, form your own judgment, and record how certain you are before submitting the rating.</p>}
        {!feedback && !preVoiceGateActive && showSessionInstruction && task.responseType !== "text" && instruction?.text && (
          <p data-copy-block={instruction.blockId}>{instruction.text}</p>
        )}
        {!feedback && !preVoiceGateActive && showSessionInstruction && task.responseType !== "text" && <JudgmentOrientation reviewPresentation={reviewPresentation} activeConstruct={activeConstruct} constructLabel={constructLabel} />}
      </header>
      {showSessionOpening && (
        <section className="byce-task__surface" data-copy-block={opening.blockId} aria-label="Session opening">
          {opening.template.split(/\n+/).map(paragraph => <p key={paragraph}>{paragraph}</p>)}
        </section>
      )}
      {error && <p role="alert">{error}</p>}
      {acknowledgementRequired && !listeningConditionsAcknowledged ? (
        <section className="byce-task__surface" aria-labelledby="listening-condition-heading">
          <h3 id="listening-condition-heading">Listening conditions</h3>
          <p>{listeningConditionCopy.acknowledgement}</p>
          {showListeningConditionExplanation && listeningConditionCopy.reminder && <p>{listeningConditionCopy.reminder}</p>}
          {confidenceExplanation && <p>{confidenceExplanation}</p>}
          {stageIntroductionAcknowledgementBlocks.map(block => (
            block.text.split(/\n+/).map(paragraph => (
              <p key={`${block.blockId}:${paragraph}`} data-copy-block={block.blockId}>{paragraph}</p>
            ))
          ))}
          <button className="app-btn app-btn-secondary" type="button" disabled={acknowledgementInFlight} onClick={acknowledgeListeningConditions}>
            {acknowledgementInFlight ? "Recording…" : "I acknowledge these listening conditions"}
          </button>
        </section>
      ) : anchorTourRequired && !anchorTourDismissed ? (
        <AnchorTour tour={anchorTour} onPlay={playAnchorTourItem} onContinue={continueFromAnchorTour} disabled={anchorTourInFlight} />
      ) : guidedIntroductionRequired && !guidedIntroductionDismissed ? (
        <section className="byce-task__surface" aria-label="Session introduction">
          <h3>Before Voice 1</h3>
          <p>{sessionStartPresentation?.introduction}</p>
          <ReferenceAnchorPlayer
            anchors={guidedIntroductionAnchors}
            headingLabel="Your reference voices"
            helpText={anchorInstruction}
            groups={guidedIntroductionAnchorGroups}
            getDisplayLabel={getBYCEAnchorDisplayLabel}
            onReplay={replay}
          />
          <button className="app-btn app-btn-secondary" type="button" onClick={continueFromGuidedIntroduction}>Continue</button>
        </section>
      ) : task.responseType === "text" && governedReflectionRequired ? (
        <section className="byce-task__surface" aria-label="Reflection">
          <h3>Reflection</h3>
          <section aria-labelledby="reflection-review-heading">
            <h4 id="reflection-review-heading">Review the two voices</h4>
            {reflectionContext?.candidateInstruction?.text && (
              <p data-copy-block={reflectionContext.candidateInstruction.blockId}>
                {reflectionContext.candidateInstruction.text}
              </p>
            )}
          </section>
          <div className="byce-task__response-stack">
            {(reflectionContext?.candidates || []).map((candidate, candidateIndex) => {
              const rows = (candidate.parameters || []).map(parameter => ({
                parameterId: parameter.parameterId,
                constructLabel: parameter.parameter,
                sourceParameterLabel: parameter.parameter,
                learnerValue: candidate.response?.[parameter.parameterId],
                reference: selectBYCEVoiceAssessmentReference(candidate.reference, parameter.parameterId),
              }));
              const variant = reflectionContext?.expertBandCapability?.variants?.find(entry => entry.trialRole === "low-consensus-target");
              const selected = selectedReflectionTrialId === candidate.sourceTrialId;
              return <article
                aria-label={`Voice ${candidateIndex + 1} candidate${selected ? ", selected" : ""}`}
                className="byce-task__surface byce-task__reflection-candidate"
                data-selected={selected}
                key={candidate.sourceTrialId}
              >
                <h4>Voice {candidateIndex + 1}</h4>
                <TargetAudioPlayer sample={{ sampleLink: candidate.audio.contentUrl }} />
                <details className="byce-task__reflection-review">
                  <summary>View reference feedback</summary>
                  <ExpertBandFeedback rows={rows} capability={reflectionContext.expertBandCapability} variant={variant}
                    presentationContract={governedG3FeedbackBand} presentationVariant={governedG3FeedbackVariant} />
                </details>
                <label className="byce-task__reflection-choice">
                  <input
                    ref={candidateIndex === 0 ? reflectionChoiceRef : undefined}
                    type="radio"
                    name="reflection-source-voice"
                    checked={selected}
                    onChange={() => setSelectedReflectionTrialId(candidate.sourceTrialId)}
                    aria-invalid={(activationValidationEnabled && validationAttempted && !selectedReflectionTrialId) || undefined}
                    aria-describedby={activationValidationEnabled && validationAttempted && !selectedReflectionTrialId ? "reflection-choice-validation" : undefined}
                  />
                  Choose Voice {candidateIndex + 1}
                </label>
              </article>;
            })}
          </div>
          {activationValidationEnabled && validationAttempted && !selectedReflectionTrialId && (
            <p id="reflection-choice-validation" className="byce-task__field-validation">Choose one Voice before submitting the Reflection.</p>
          )}
          <section className="byce-task__reflection-response" aria-labelledby="reflection-response-heading">
            <h4 id="reflection-response-heading">Your reflection</h4>
            {!selectedReflectionTrialId && <p>Choose one voice above to begin.</p>}
            {reflectionPromptAvailable && (
              <>
              {exactReflectionPrompt ? (
                <p data-copy-block="ST-32">{exactReflectionPrompt}</p>
              ) : (
                <section aria-labelledby="reflection-provisional-heading" data-copy-owner="governor" data-copy-status="provisional-internal-review">
                  <h4 id="reflection-provisional-heading">{provisionalReflectionPrompt.title}</h4>
                  <p>{provisionalReflectionPrompt.text}</p>
                </section>
              )}
              {presentedStep?.learnerUnit?.presentation?.reflectionCredit?.text && (
                <p data-copy-block="ST-33">{presentedStep.learnerUnit.presentation.reflectionCredit.text}</p>
              )}
              <textarea
                ref={reflectionTextRef}
                aria-label="Reflection response"
                aria-invalid={(activationValidationEnabled && validationAttempted && text.trim().length < reflectionMinimum) || undefined}
                aria-describedby={`reflection-character-limit${activationValidationEnabled && validationAttempted && text.trim().length < reflectionMinimum ? " reflection-text-validation" : ""}`}
                maxLength={task.responseConfig.maxCharacters}
                value={text}
                onChange={event => setText(event.target.value)}
              />
              <p id="reflection-character-limit">Minimum {task.responseConfig.minCharacters} characters; maximum {task.responseConfig.maxCharacters} characters</p>
              <p aria-live="polite">{text.length} / {task.responseConfig.maxCharacters} characters</p>
              {activationValidationEnabled && validationAttempted && text.trim().length < reflectionMinimum && (
                <p id="reflection-text-validation" className="byce-task__field-validation">Write at least {reflectionMinimum} characters before submitting the Reflection.</p>
              )}
              </>
            )}
            {activationValidationEnabled && <ValidationSummary issues={validationAttempted ? reflectionValidationIssues : []} />}
            <button
              className="app-btn app-btn-secondary"
              type="button"
              disabled={submissionInFlight || (!activationValidationEnabled && (!reflectionPromptAvailable || text.trim().length < task.responseConfig.minCharacters))}
              onClick={() => activationValidationEnabled
                ? activateValidatedSubmission(reflectionValidationIssues, submitReflection)
                : submitReflection()}
            >Submit reflection</button>
          </section>
        </section>
      ) : task.responseType === "text" ? (
        <section className="byce-task__surface">
          <h3>{t(task.titleKey, { defaultValue: task.title || task.titleKey })}</h3>
          <p>{isSessionReviewConsumer
            ? BYCE_SESSION_REVIEW_PRESENTATION.reflectionPrompt
            : "What did the source distribution show about genuine expert variation?"}</p>
          <textarea aria-label="Reflection response" aria-describedby="reflection-character-limit" maxLength={task.responseConfig.maxCharacters} value={text} onChange={event => setText(event.target.value)} />
          <p id="reflection-character-limit">Maximum {task.responseConfig.maxCharacters} characters</p>
          <button className="app-btn app-btn-secondary" type="button" disabled={submissionInFlight || !text.trim()} onClick={submitReflection}>Submit reflection</button>
        </section>
      ) : config.constructs?.length && !config.trialConstructPlan && judgmentPhase ? (
        <section ref={supportContextRef} tabIndex="-1" className="byce-task__response-stack" aria-label="CAPE-Vr perceptual judgments and confidence">
          {task.uiConfig?.referenceFeedbackEnabled === false && <div className="byce-task__unsupported"><strong>On your own</strong><br />Anchors and immediate feedback are unavailable in this stage.</div>}
          {config.constructs.map((construct, constructIndex) => {
            const presentation = getBYCEConstructPresentation(construct.id);
            const label = construct.label || presentation.label;
            const ratingMissing = vectorValues[construct.id] === "" || vectorValues[construct.id] === undefined;
            const confidenceMissing = vectorConfidence[construct.id] === undefined;
            const ratingMessage = missingRatingTemplate?.replace("{parameter}", label) || `Choose a ${label} rating before submitting.`;
            const confidenceMessage = `Choose a confidence rating for ${label}.`;
            return <section className="byce-task__surface" aria-label={`${label} judgment`} key={construct.id}>
              <h3>{label}</h3>
              <ReferenceAnchorPlayer
                anchors={anchors}
                currentConstructId={construct.id}
                constructLabel={label}
                headingLabel="Reference anchors"
                helpText={anchorInstruction}
                getDisplayLabel={getBYCEAnchorDisplayLabel}
                onReplay={replay}
              />
              <PerceptualResponseInput
                constructLabel={label}
                instruction={presentation.instruction}
                responseFamilyLabel="continuous 0–100 rating"
                sourceParameterLabel={label}
                scaleFamily="continuous"
                min={construct.min}
                max={construct.max}
                integer={false}
                value={vectorValues[construct.id] === "" ? undefined : vectorValues[construct.id]}
                onChange={next => setVectorValues(current => ({ ...current, [construct.id]: next }))}
                endpointLabels={{ min: "Normal", max: "Extreme" }}
                visuallyBlindValue
                hideHandleUntilInteraction
                visualInitialValue={0}
                showIdentity={false}
                leadingContent={<TargetAudioPlayer
                  sample={sample}
                  constructLabel={label}
                  onPlay={recordTargetPlay}
                  inputRef={constructIndex === 0 ? targetPlaybackRef : undefined}
                  invalid={constructIndex === 0 && activationValidationEnabled && validationAttempted && Boolean(targetPlaybackIssue)}
                  validationMessage={targetPlaybackMessage || "Play the target voice before submitting."}
                />}
                sectionLabel="Rating"
                inputRef={element => { vectorRatingRefs.current[construct.id] = element; }}
                invalid={activationValidationEnabled && validationAttempted && ratingMissing}
                validationMessage={ratingMessage}
              />
              <LearnerConfidenceInput
                capability={capabilities.learnerConfidence}
                constructId={construct.id}
                scaleFamily="continuous"
                value={vectorConfidence[construct.id]}
                onChange={next => setVectorConfidence(current => ({ ...current, [construct.id]: next }))}
                midpointLabel={confidenceMidpointLabel}
                hideHandleUntilInteraction
                inputRef={element => { vectorConfidenceRefs.current[construct.id] = element; }}
                invalid={activationValidationEnabled && validationAttempted && confidenceMissing}
                validationMessage={confidenceMessage}
              />
            </section>;
          })}
          <div className="byce-task__actions">
            {!activationValidationEnabled && !canSubmitVector && <p role="status">{
              firstMissingVectorConstruct
                ? vectorMissingRatingMessage
                : targetPlaybackGateRequired && !targetPlaybackSatisfied ? targetPlaybackMessage : null
            }</p>}
            {activationValidationEnabled && <ValidationSummary issues={validationAttempted ? vectorValidationIssues : []} />}
            <button className="app-btn app-btn-secondary" type="button" disabled={submissionInFlight || (!activationValidationEnabled && !canSubmitVector)} onClick={() => activationValidationEnabled
              ? activateValidatedSubmission(vectorValidationIssues, submitVector)
              : submitVector()}>Submit ratings</button>
          </div>
        </section>
      ) : capabilities.pairedScale && judgmentPhase ? (
        <section ref={supportContextRef} tabIndex="-1" className="byce-task__response-stack">
          <ReferenceAnchorPlayer
            anchors={anchors}
            currentConstructId={activeConstruct}
            constructLabel={constructLabel}
            headingLabel="Reference anchors"
            helpText={anchorInstruction}
            getDisplayLabel={getBYCEAnchorDisplayLabel}
            onReplay={replay}
          />
          <TargetAudioPlayer sample={sample} constructLabel={constructLabel} onPlay={recordTargetPlay} />
          <PairedScaleResponseInput
            capability={capabilities.pairedScale}
            learnerConfidenceCapability={capabilities.learnerConfidence}
            state={paired}
            primaryValue={pairedValue}
            confidenceValue={pairedConfidence}
            onPrimaryChange={setPairedValue}
            onConfidenceChange={setPairedConfidence}
            onSave={() => pairedAction("save")}
            onLock={() => pairedAction("lock")}
            constructPresentations={BYCE_CONSTRUCT_PRESENTATIONS}
            categoricalOptions={BYCE_CATEGORICAL_OPTIONS}
            continuousEndpointLabels={{ min: "Normal", max: "Extreme" }}
            visuallyBlindContinuousValue
            bridgeHelp={activeConstruct === "global-voice-severity" ? <GlobalSeverityBridgeHelp /> : null}
          />
        </section>
      ) : judgmentPhase ? (
        <section className="byce-task__response-stack" aria-label="Perceptual judgment and confidence">
          {task.uiConfig?.referenceFeedbackEnabled === false && <div className="byce-task__unsupported"><strong>Transfer capstone</strong><br />This stage is completed without reference anchors or supportive feedback.</div>}
          <section className="byce-task__surface" aria-label={`${constructLabel} judgment`}>
            <h3>{constructLabel}</h3>
            <ReferenceAnchorPlayer
              anchors={anchors}
              currentConstructId={capabilities.referenceAnchors?.presentation?.ordinaryDisplay === "grouped-all-parameters" ? undefined : activeConstruct}
              constructLabel={constructLabel}
              headingLabel="Reference anchors"
              helpText={anchorInstruction}
              groups={capabilities.referenceAnchors?.presentation?.groups}
              getDisplayLabel={getBYCEAnchorDisplayLabel}
              onReplay={replay}
            />
            <PerceptualResponseInput
              constructLabel={constructLabel}
              instruction={constructPresentation.instruction}
              responseFamilyLabel={scalePresentation.responseFamilyLabel}
              sourceParameterLabel={scalePresentation.sourceParameterLabel}
              options={BYCE_CATEGORICAL_OPTIONS}
              scaleFamily={config.scaleFamily}
              min={config.min}
              max={config.max}
              integer={Boolean(config.integer)}
              value={value === "" ? undefined : value}
              onChange={setValue}
              endpointLabels={config.scaleFamily === "continuous" ? { min: "Normal", max: "Extreme" } : undefined}
              visuallyBlindValue={config.scaleFamily === "continuous"}
              hideHandleUntilInteraction={config.scaleFamily === "continuous"}
              visualInitialValue={config.scaleFamily === "continuous" ? 0 : undefined}
              showIdentity={config.scaleFamily !== "continuous"}
              leadingContent={<TargetAudioPlayer
                sample={sample}
                constructLabel={constructLabel}
                onPlay={recordTargetPlay}
                inputRef={targetPlaybackRef}
                invalid={activationValidationEnabled && validationAttempted && Boolean(targetPlaybackIssue)}
                validationMessage={targetPlaybackMessage || "Play the target voice before submitting."}
              />}
              sectionLabel="Rating"
              inputRef={simpleRatingRef}
              invalid={activationValidationEnabled && validationAttempted && (value === "" || value === undefined)}
              validationMessage={missingRatingMessage}
            />
            <LearnerConfidenceInput
              capability={capabilities.learnerConfidence}
              constructId={simpleConstructId}
              scaleFamily={config.scaleFamily}
              value={confidence}
              onChange={setConfidence}
              midpointLabel={confidenceMidpointLabel}
              hideHandleUntilInteraction
              inputRef={simpleConfidenceRef}
              invalid={activationValidationEnabled && validationAttempted && confidence === undefined}
            />
          </section>
          <div className="byce-task__actions">
            {!activationValidationEnabled && !canSubmitSimple && <p role="status">{
              value === "" || value === undefined
                ? missingRatingMessage
                : targetPlaybackGateRequired && !targetPlaybackSatisfied ? targetPlaybackMessage : null
            }</p>}
            {activationValidationEnabled && <ValidationSummary issues={validationAttempted ? simpleValidationIssues : []} />}
            <button className="app-btn app-btn-secondary" type="button" disabled={submissionInFlight || (!activationValidationEnabled && !canSubmitSimple)} onClick={() => activationValidationEnabled
              ? activateValidatedSubmission(simpleValidationIssues, submitSimple)
              : submitSimple()}>Submit rating</button>
          </div>
        </section>
      ) : null}
      {lockedTransitionPending && <p role="status">Recovering your saved response…</p>}
      {currentResponseLocked && error && !feedback && (
        <button className="app-btn app-btn-secondary" type="button" onClick={retryLockedTransition}>Retry saved feedback</button>
      )}

      {feedback?.reference && config.scaleFamily === "categorical" && categoricalFeedback.reference && (
        <>
          <div role="region" aria-label="Reference feedback start">
            <CategoricalReferenceFeedback
              {...categoricalFeedback}
              sourceHelp={<CategoricalReferenceHelp sourceParameterLabel={categoricalFeedback.sourceParameterLabel} />}
            />
          </div>
          <div className="byce-task__actions"><button className="app-btn app-btn-secondary" type="button" onClick={complete}>Continue</button></div>
        </>
      )}
      {feedback?.probeFirstPass && (
        <section className="byce-task__surface" aria-label="Response submitted">
          <p data-copy-block={feedback.probeFirstPass.blockId}>{feedback.probeFirstPass.message}</p>
          <div className="byce-task__actions"><button className="app-btn app-btn-secondary" type="button" onClick={complete}>Continue</button></div>
        </section>
      )}
      {feedback?.reference && config.scaleFamily === "continuous" && (!config.constructs?.length || config.trialConstructPlan) && !capabilities.pairedScale && (
        <>
          <div role="region" aria-label="Reference feedback start">
            {expertBandCapability && expertBandVariant
              ? <ExpertBandFeedback rows={simpleContinuousFeedbackRows} capability={expertBandCapability} variant={expertBandVariant} mirrorRows={mirror?.rows} mirrorCopy={mirrorCopy} firstPassDate={firstPassDate} showStandaloneTargetReplayInstruction={showStandaloneTargetReplayInstruction} presentationContract={governedG3FeedbackBand} presentationVariant={governedG3FeedbackVariant} headingHelp={<ExpertBandReferenceHelp />}
                  targetReplay={<TargetAudioPlayer sample={sample} constructLabel={constructLabel} onPlay={recordTargetPlay} />} />
              : <><TargetAudioPlayer sample={sample} constructLabel={constructLabel} onPlay={recordTargetPlay} /><ContinuousReferenceFeedback sourceLabel="PVQD reference ratings" rows={simpleContinuousFeedbackRows} sourceHelp={<ContinuousReferenceHelp />} graphical compact /></>}
          </div>
          <div className="byce-task__actions"><button className="app-btn app-btn-secondary" type="button" onClick={complete}>Continue</button></div>
        </>
      )}
      {feedback?.reference && capabilities.pairedScale && (
        <>
          <div role="region" aria-label="Reference feedback start">
            {pairedCategoricalFeedbackRows.map(row => (
              <CategoricalReferenceFeedback
                key={row.constructId}
                {...row}
                sourceHelp={<CategoricalReferenceHelp sourceParameterLabel={row.sourceParameterLabel} />}
              />
            ))}
            <ContinuousReferenceFeedback
              sourceLabel="PVQD larger-panel ratings"
              rows={pairedContinuousFeedbackRows}
              sourceHelp={<ContinuousReferenceHelp />}
            />
          </div>
          <div className="byce-task__actions"><button className="app-btn app-btn-secondary" type="button" onClick={complete}>Continue</button></div>
        </>
      )}
      {feedback?.reference && config.constructs?.length && !config.trialConstructPlan && (
        <>
          <div role="region" aria-label="Reference feedback start">
            {expertBandCapability && expertBandVariant
              ? <ExpertBandFeedback rows={vectorContinuousFeedbackRows} capability={expertBandCapability} variant={expertBandVariant} mirrorRows={mirror?.rows} mirrorCopy={mirrorCopy} firstPassDate={firstPassDate} showStandaloneTargetReplayInstruction={showStandaloneTargetReplayInstruction} presentationContract={governedG3FeedbackBand} presentationVariant={governedG3FeedbackVariant} headingHelp={<ExpertBandReferenceHelp />}
                  targetReplay={<TargetAudioPlayer sample={sample} onPlay={recordTargetPlay} />} />
              : <><TargetAudioPlayer sample={sample} onPlay={recordTargetPlay} /><ContinuousReferenceFeedback sourceLabel="PVQD reference ratings" rows={vectorContinuousFeedbackRows} sourceHelp={<ContinuousReferenceHelp />} graphical compact /></>}
          </div>
          <div className="byce-task__actions"><button className="app-btn app-btn-secondary" type="button" onClick={complete}>Continue</button></div>
        </>
      )}
      {feedback?.projections && <><TargetAudioPlayer sample={sample} onPlay={recordTargetPlay} /><div role="region" aria-label="Reference feedback start"><AmbiguityDistributionFeedback projection={feedback} getConstructLabel={id => getBYCEConstructPresentation(id).label} primarySourceLabel="PVQD reference ratings" headingHelp={<ContinuousReferenceHelp />} /></div><div className="byce-task__actions"><button className="app-btn app-btn-secondary" type="button" onClick={complete}>{currentIndex + 1 >= totalTrials ? finalFeedbackActionLabel : "Continue"}</button></div></>}
      {!(acknowledgementRequired && !listeningConditionsAcknowledged) && <CitationsPanel
        voiceDatabase={citationReferences.voiceDatabase || pvqdVoiceDatabaseCitation}
        voiceCollection={citationReferences.voiceCollection || null}
        representationTransformations={citationReferences.representationTransformations || []}
      />}
    </main>
  );
}
